import { Car, Plus, Trash2 } from 'lucide-react';
import { Section, Field, Toggle, CBx, EditButton, EditActions } from '../ui';
import { ic } from '../helpers';
import { OWNERSHIP_TYPES, VEHICLE_TYPES } from '../constants';

// ─────────────────────────────────────────
// VEHICLES SECTION
// One card per auto with read/edit toggle. Includes the classic-car
// (year < 1981) Agreed Value override, plus discount and anti-theft
// checkbox grids.
// ─────────────────────────────────────────
export default function VehiclesSection({
  client,
  autos,
  editingAutoId,
  autoDraft,
  setAutoDraft,
  saving,
  onAddAuto,
  onStartEditAuto,
  onSaveAuto,
  onCancelEditAuto,
  onDeleteAuto,
}) {
  return (
    <Section
      title={`Vehicles (${autos.length})`}
      icon={Car}
      action={
        <button
          onClick={onAddAuto}
          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
        >
          <Plus size={11} /> Add Vehicle
        </button>
      }
    >
      <div className="space-y-3 pt-3">
        {autos.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No vehicles added yet.</p>
        )}

        {autos.map((auto) => {
          const isEditing = editingAutoId === auto.id;
          const draft = isEditing ? autoDraft : auto;
          const update = (key, val) => setAutoDraft(p => ({ ...p, [key]: val }));
          const discountLabels = [
            auto.discountABS && 'ABS',
            auto.discountPassiveRestraint && 'Passive Restraint',
          ].filter(Boolean).join(', ');
          const antitheftLabels = [
            auto.antitheftVinEtching && 'VIN Etching',
            auto.antitheftVehicleRecovery && 'Vehicle Recovery',
            auto.antitheftDisablingDevice && 'Disabling Device',
          ].filter(Boolean).join(', ');

          return (
            <div key={auto.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                <div className="flex items-center gap-2 min-w-0">
                  <Car size={14} className="text-blue-600 shrink-0" />
                  <span className="text-sm font-semibold text-slate-700">
                    {[auto.year, auto.make, auto.model].filter(Boolean).join(' ') || 'New Vehicle'}
                  </span>
                  {auto.ownershipType && (
                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-lg ml-1 shrink-0">
                      {auto.ownershipType}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isEditing
                    ? <EditActions onSave={() => onSaveAuto(auto.id)} onCancel={onCancelEditAuto} saving={saving} />
                    : <>
                      <EditButton onClick={() => onStartEditAuto(auto)} />
                      <button onClick={() => onDeleteAuto(auto.id)} className="p-1 text-slate-400 hover:text-red-500 transition">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                </div>
              </div>
              <div className="p-4 space-y-4">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      {['year','make','model'].map(key => (
                        <div key={key}>
                          <label className="block text-xs font-medium text-slate-500 mb-1 capitalize">{key}</label>
                          <input type={key === 'year' ? 'number' : 'text'} value={draft[key] || ''} onChange={e => update(key, e.target.value)} className={ic} />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">VIN</label>
                      <input
                        value={draft.vin || ''}
                        onChange={e => update('vin', e.target.value.toUpperCase())}
                        maxLength={17}
                        placeholder="17-character VIN"
                        className={`${ic} font-mono tracking-wider uppercase`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Trim / Spec</label>
                      <input value={draft.trim || ''} onChange={e => update('trim', e.target.value)} placeholder="e.g. EX-L · 2.5L I4 · 4-door · AWD" className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Vehicle Type</label>
                      <select value={draft.vehicleType || 'Private Passenger'} onChange={e => update('vehicleType', e.target.value)} className={ic}>
                        {VEHICLE_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                      <p className="text-xs text-slate-400 mt-1">
                        Used by the autofill to set the carrier's vehicle type. Most cars/SUVs/trucks should be "Private Passenger".
                      </p>
                    </div>
                    {draft.year && parseInt(draft.year) < 1981 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <label className="block text-xs font-semibold text-amber-800 mb-1">
                          ✦ Agreed Value ($) <span className="font-normal text-amber-600">— AI estimated, agent can override</span>
                        </label>
                        <input type="number" value={draft.agreedValue || ''} onChange={e => update('agreedValue', e.target.value)} placeholder="e.g. 18000" className={ic} />
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Name on Title</label>
                        <input value={draft.titleHolder || ''} onChange={e => update('titleHolder', e.target.value)} className={ic} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Primary Driver</label>
                        <input value={draft.primaryDriver || ''} onChange={e => update('primaryDriver', e.target.value)} className={ic} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Ownership</label>
                        <select value={draft.ownershipType || 'Owned'} onChange={e => update('ownershipType', e.target.value)} className={ic}>
                          {OWNERSHIP_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Vehicle Details</p>
                      </div>
                      <div className="px-4 divide-y divide-slate-100">
                        <Toggle label="Custom / additional equipment to be insured?" checked={!!draft.hasCustomEquipment} onChange={v => update('hasCustomEquipment', v)} editing={true} />
                        {draft.hasCustomEquipment && (
                          <div className="py-2.5">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Custom Equipment Coverage Amount ($)</label>
                            <input type="number" value={draft.customEquipmentAmount || ''} onChange={e => update('customEquipmentAmount', e.target.value)} className={ic} placeholder="e.g. 5000" />
                          </div>
                        )}
                        <Toggle label="Kept in a garage with 24-hour security?" checked={!!draft.keptInGarage} onChange={v => update('keptInGarage', v)} editing={true} />
                        <Toggle label={`Registered in ${client.riskState || 'home state'}?`} checked={!!draft.registeredInState} onChange={v => update('registeredInState', v)} editing={true} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Discounts</p>
                        </div>
                        <div className="px-3 py-2 space-y-0.5">
                          <CBx label="ABS" checked={!!draft.discountABS} onChange={v => update('discountABS', v)} />
                          <CBx label="Passive Restraint" checked={!!draft.discountPassiveRestraint} onChange={v => update('discountPassiveRestraint', v)} />
                        </div>
                      </div>
                      <div className="border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Anti-Theft</p>
                        </div>
                        <div className="px-3 py-2 space-y-0.5">
                          <CBx label="VIN Etching" checked={!!draft.antitheftVinEtching} onChange={v => update('antitheftVinEtching', v)} />
                          <CBx label="Vehicle Recovery" checked={!!draft.antitheftVehicleRecovery} onChange={v => update('antitheftVehicleRecovery', v)} />
                          <CBx label="Disabling Device" checked={!!draft.antitheftDisablingDevice} onChange={v => update('antitheftDisablingDevice', v)} />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Year" value={auto.year} editing={false} />
                    <Field label="Make" value={auto.make} editing={false} />
                    <Field label="Model" value={auto.model} editing={false} />
                    {auto.trim && (
                      <div className="col-span-3">
                        <p className="text-xs font-medium text-slate-400 mb-0.5">Trim / Spec</p>
                        <p className="text-sm font-medium text-slate-800">{auto.trim}</p>
                      </div>
                    )}
                    <div className="col-span-3">
                      <p className="text-xs font-medium text-slate-400 mb-0.5">VIN</p>
                      <p className="text-sm font-mono tracking-wider text-slate-800">
                        {auto.vin || <span className="text-slate-400 font-normal font-sans">—</span>}
                      </p>
                    </div>
                    <div className="col-span-3">
                      <p className="text-xs font-medium text-slate-400 mb-0.5">Vehicle Type</p>
                      <p className="text-sm font-medium text-slate-800">{auto.vehicleType || 'Private Passenger'}</p>
                    </div>
                    {auto.year && parseInt(auto.year) < 1981 && (
                      <div className="col-span-3">
                        <p className="text-xs font-medium text-slate-400 mb-0.5">Agreed Value (Classic Car)</p>
                        <p className="text-sm font-medium text-slate-800">
                          {auto.agreedValue
                            ? `$${Number(auto.agreedValue).toLocaleString()}`
                            : <span className="text-slate-400 font-normal">—</span>}
                        </p>
                      </div>
                    )}
                    <Field label="Title Holder" value={auto.titleHolder} editing={false} />
                    <Field label="Primary Driver" value={auto.primaryDriver} editing={false} />
                    <Field label="Ownership" value={auto.ownershipType} editing={false} />
                    <Field
                      label="Custom Equipment"
                      value={auto.hasCustomEquipment
                        ? `Yes${auto.customEquipmentAmount ? ` — $${Number(auto.customEquipmentAmount).toLocaleString()}` : ''}`
                        : 'No'}
                      editing={false}
                    />
                    <Field label="24hr Garage" value={auto.keptInGarage ? 'Yes' : 'No'} editing={false} />
                    <Field
                      label={`Registered in ${client.riskState || 'State'}`}
                      value={auto.registeredInState ? 'Yes' : 'No'}
                      editing={false}
                    />
                    {discountLabels && (
                      <div className="col-span-3">
                        <p className="text-xs font-medium text-slate-400 mb-0.5">Discounts</p>
                        <p className="text-sm font-medium text-slate-800">{discountLabels}</p>
                      </div>
                    )}
                    {antitheftLabels && (
                      <div className="col-span-3">
                        <p className="text-xs font-medium text-slate-400 mb-0.5">Anti-Theft</p>
                        <p className="text-sm font-medium text-slate-800">{antitheftLabels}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}